import React, { useEffect, useMemo, useState } from "react";
import { useGetNotificationSendsQuery } from "../../../app/api/console/consoleCustomerAnalyticsApiSlice";
import { hubActivityRunUrl } from "../ControlTower/hubScanLinks";
import {
  PAGE_SIZE,
  SEND_RANGES,
  SEND_STATES,
  engagementSummary,
  formatLag,
  hoursSince,
  rangeToWindow,
  sendsToCsv,
  summarizeEngagement,
} from "./sendDetailsHelpers";

const ENGAGEMENT_STYLES = {
  visited: "bg-accent-green/10 text-accent-green border-accent-green/20",
  none: "bg-gray-100 text-text-secondary border-border-subtle",
  unobservable: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  skipped: "bg-gray-50 text-text-tertiary border-border-subtle",
  unknown: "bg-gray-50 text-text-tertiary border-border-subtle",
};

function formatTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Searchable, paged run-notification history with per-send engagement. Independent
// of the page-wide time range: goes back over the whole history on request.
export default function SendDetailsPanel({ useProd = true, websiteNames = {}, onInspect }) {
  const [preset, setPreset] = useState("90d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [search, setSearch] = useState("");
  const [state, setState] = useState("");
  const [page, setPage] = useState(0);
  const q = useDebounced(search.trim());

  const window = useMemo(() => rangeToWindow(preset, customStart, customEnd), [preset, customStart, customEnd]);
  useEffect(() => setPage(0), [preset, customStart, customEnd, q, state, useProd]);

  const { data, isLoading, isFetching, error } = useGetNotificationSendsQuery({
    start: window.start,
    end: window.end,
    q,
    state,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    useProd,
  }, {
    // Sends made from elsewhere (review queue, MCP) must show up without a reload.
    refetchOnMountOrArgChange: true,
    pollingInterval: 60000,
  });
  const items = useMemo(() => (data && data.items) || [], [data]);
  const total = (data && data.total) || 0;
  const windowDays = (data && data.engagement_window_days) || 14;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const engagement = useMemo(() => summarizeEngagement(items), [items]);
  const env = useProd ? "prod" : "staging";
  const websiteName = (wid) => websiteNames[wid] || wid;

  const exportCsv = () => {
    const blob = new Blob([sendsToCsv(items)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notification_sends_${env}_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const control =
    "px-3 py-1.5 text-body-xs bg-white border border-border-default rounded-lg text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-purple/20";

  return (
    <div className='bg-white rounded-2xl border border-border-subtle p-5 space-y-4' data-testid='send-details'>
      <div className='flex items-start justify-between gap-3 flex-wrap border-b border-border-subtle pb-3'>
        <div>
          <h3 className='text-body-sm font-bold text-text-primary'>Send Details</h3>
          <p className='text-caption text-text-tertiary mt-0.5'>
            Every run-complete email, as far back as the history goes. Engagement = a recipient with a Hub account reaching the
            announced run within {windowDays} days; "Opened from email" means a fresh Hub session started on that run page.
            Emails carry no open-tracking pixel.
          </p>
        </div>
        <span className='text-[11px] font-semibold text-text-tertiary bg-gray-100 px-2 py-0.5 rounded-md whitespace-nowrap'>
          {total.toLocaleString()} send{total === 1 ? "" : "s"} · {env}
        </span>
      </div>

      <div className='flex flex-wrap gap-2 items-end'>
        <div>
          <label htmlFor='sd-range' className='block text-caption font-medium text-text-secondary mb-1'>
            Period
          </label>
          <select id='sd-range' value={preset} onChange={(e) => setPreset(e.target.value)} className={control}>
            {SEND_RANGES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        {preset === "custom" && (
          <>
            <div>
              <label htmlFor='sd-start' className='block text-caption font-medium text-text-secondary mb-1'>
                From
              </label>
              <input id='sd-start' type='date' value={customStart} onChange={(e) => setCustomStart(e.target.value)} className={control} />
            </div>
            <div>
              <label htmlFor='sd-end' className='block text-caption font-medium text-text-secondary mb-1'>
                To
              </label>
              <input id='sd-end' type='date' value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className={control} />
            </div>
          </>
        )}
        <div>
          <label htmlFor='sd-state' className='block text-caption font-medium text-text-secondary mb-1'>
            State
          </label>
          <select id='sd-state' value={state} onChange={(e) => setState(e.target.value)} className={control}>
            {SEND_STATES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className='flex-1 min-w-[240px]'>
          <label htmlFor='sd-search' className='block text-caption font-medium text-text-secondary mb-1'>
            Search
          </label>
          <input
            id='sd-search'
            type='search'
            placeholder='email, subject, run id, scan id, website id…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${control} w-full`}
          />
        </div>
        <button
          type='button'
          onClick={exportCsv}
          disabled={items.length === 0}
          className='px-3 py-1.5 text-body-xs font-semibold bg-accent-purple/10 text-accent-purple rounded-lg hover:bg-accent-purple/20 disabled:opacity-40'>
          Export page CSV
        </button>
      </div>

      {error ? (
        <div className='border border-dashed border-border-default rounded-xl p-6 text-center'>
          <p className='text-body-sm font-semibold text-text-secondary'>Send history unavailable in {env}</p>
          <p className='text-caption text-text-tertiary mt-1'>
            {(error.data && error.data.detail) || "The Platform search endpoint returned an error."}
          </p>
        </div>
      ) : (
        <>
          <div className='flex items-center justify-between flex-wrap gap-2 text-caption text-text-secondary'>
            <span role='status'>
              {isLoading ? "Loading…" : isFetching ? "Updating…" : null}
              {!isLoading && engagement.sent > 0 && (
                <>
                  On this page: {engagement.visited} of {engagement.observable} observable send{engagement.observable === 1 ? "" : "s"} visited
                  {engagement.rate != null ? ` (${Math.round(engagement.rate * 100)}%)` : ""}
                  {engagement.fromLink > 0 ? ` · ${engagement.fromLink} opened from the email` : ""}
                  {engagement.sent - engagement.observable > 0 ? ` · ${engagement.sent - engagement.observable} with no Hub account` : ""}
                </>
              )}
            </span>
            <span className='flex items-center gap-2'>
              <button type='button' onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className={`${control} disabled:opacity-40`}>
                ‹ Newer
              </button>
              <span>
                Page {Math.min(page + 1, pageCount)} of {pageCount}
              </span>
              <button
                type='button'
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page + 1 >= pageCount}
                className={`${control} disabled:opacity-40`}>
                Older ›
              </button>
            </span>
          </div>

          <div className='border border-border-subtle rounded-xl overflow-hidden'>
            <div className='overflow-x-auto max-h-[560px]'>
              <table className='w-full text-left text-body-xs font-semibold'>
                <thead>
                  <tr className='bg-surface-neutral/60 border-b border-border-subtle'>
                    <th className='px-3 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-40'>Sent At</th>
                    <th className='px-3 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-36'>Website</th>
                    <th className='px-3 py-2.5 font-bold text-text-secondary uppercase tracking-wider'>Subject</th>
                    <th className='px-3 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-28'>State</th>
                    <th className='px-3 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-56'>Recipients</th>
                    <th className='px-3 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-48'>Engagement</th>
                    <th className='px-3 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-28'>Run</th>
                    <th className='px-3 py-2.5 w-16'></th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border-subtle'>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={8} className='px-4 py-8 text-center text-text-tertiary font-normal'>
                        {isLoading ? "Loading send history…" : "No sends match these filters."}
                      </td>
                    </tr>
                  ) : (
                    items.map((event) => {
                      const summary = engagementSummary(event, windowDays);
                      const visitors = new Set(((event.engagement && event.engagement.visitors) || []).map((v) => v.toLowerCase()));
                      const lag = formatLag(hoursSince(event.sent_at, summary.firstVisitAt));
                      return (
                        <tr key={event.event_id} className='hover:bg-surface-neutral/30 transition-colors align-top'>
                          <td className='px-3 py-3 whitespace-nowrap text-text-secondary font-mono text-[11px]'>
                            {formatTime(event.sent_at || event.created_at)}
                            <div className='text-[10px] text-text-tertiary font-sans'>{event.fire_path || "legacy"}</div>
                          </td>
                          <td className='px-3 py-3 text-[11px] text-text-primary truncate max-w-[160px]' title={event.website_id}>
                            {websiteName(event.website_id)}
                          </td>
                          <td className='px-3 py-3'>
                            <div className='text-text-primary text-[11px] truncate max-w-[300px]' title={event.subject || ""}>
                              {event.subject || "—"}
                            </div>
                            {event.is_override && (
                              <span className='inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/10 text-amber-700 border border-amber-500/20'>
                                resend
                              </span>
                            )}
                          </td>
                          <td className='px-3 py-3 whitespace-nowrap'>
                            <span
                              className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full border ${
                                event.state === "all_clear"
                                  ? "bg-accent-green/10 text-accent-green border-accent-green/20"
                                  : event.state === "skipped"
                                  ? "bg-gray-100 text-text-secondary border-border-subtle"
                                  : "bg-accent-purple/10 text-accent-purple border-accent-purple/20"
                              }`}
                              title={event.skip_reason || ""}>
                              {event.state}
                            </span>
                          </td>
                          <td className='px-3 py-3'>
                            <div className='flex flex-wrap gap-1 max-w-[240px]'>
                              {(event.recipients || []).length === 0 && <span className='text-text-tertiary'>—</span>}
                              {(event.recipients || []).map((r) => {
                                const seen = visitors.has((r || "").toLowerCase());
                                return (
                                  <span
                                    key={r}
                                    title={seen ? `${r} reached the run` : r}
                                    className={`font-mono text-[10px] px-1.5 py-0.5 rounded border ${
                                      seen ? "bg-accent-green/10 text-accent-green border-accent-green/20" : "bg-gray-50 text-text-secondary border-border-subtle"
                                    }`}>
                                    {seen ? "✓ " : ""}
                                    {r}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td className='px-3 py-3'>
                            <span
                              className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full border ${ENGAGEMENT_STYLES[summary.kind] || ENGAGEMENT_STYLES.unknown}`}
                              title={summary.detail}>
                              {summary.label}
                            </span>
                            {summary.kind === "visited" && (
                              <div className='text-[10px] text-text-tertiary mt-0.5'>
                                {lag}
                                {event.engagement && event.engagement.visit_count > 1 ? ` · ${event.engagement.visit_count} events` : ""}
                              </div>
                            )}
                          </td>
                          <td className='px-3 py-3 font-mono text-[10px]'>
                            {event.run_id ? (
                              <a
                                href={hubActivityRunUrl(env, event.run_id)}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-accent-purple hover:underline truncate block max-w-[110px]'
                                title={`Open ${event.run_id} on the Hub`}>
                                {event.run_id} ↗
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className='px-3 py-3 whitespace-nowrap text-right'>
                            {onInspect && (
                              <button type='button' onClick={() => onInspect(event)} className='text-accent-purple hover:opacity-85 font-semibold text-body-xs'>
                                Inspect
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
